import { EventAggregator, inject, observable, resolve } from 'aurelia';
import { IRouter, IRouteViewModel, Params, route, RouteNode } from '@aurelia/router-lite';
import { IStore } from '@aurelia/state';

import { apiEndpoint, getAWSBucketEndpoint, websiteShortCode } from '../../../environment';
import { DEVICES } from '../../../resources/constants';
import { BlogService } from '../../../services/blog-service';
import { WebsiteService } from '../../../services/website-service';
import { ActionNames, UpdateMetaDescription, UpdateMetaTitle, UpdateOgType } from '../../../state';
import { Helper } from '../../../resources/extensions';

import './blogpost.scss';
import { NameFromShortcodeValueConverter } from '../../../resources/value-converters';

@route({
    path: ['blog/:blog'],
    title: 'Blogs',
})
@inject(IRouter, BlogService, WebsiteService, EventAggregator, IStore, Helper, NameFromShortcodeValueConverter)
export class Blogpost implements IRouteViewModel {
    baseAwsEndpoint: string;
    blogs;
    blog;
    blogTag;
    count = 1;
    environment = apiEndpoint();
    relatedArticles;
    blogsCount: number;
    arrowTriggered;
    arrowRotated: boolean;
    lastEventTriggered: string;
    blogRoute: string;
    isAttached = false;
    @observable page: number = 1;
    perPage: number;
    pageLoading: boolean = true;
    breadcrumbs: { title: string; href: string; tooltip?: boolean }[] = [];
    homePageRoute;
    readingTimeResult = 0;
    sizeChanged;
    width;
    params;
    currentRouteNode;
    DEVICES;
    scrollView: any;
    skeletonPostContent: any[] = Array.from({ length: 4 });
    shortCode: string = websiteShortCode() ?? 'CG';

    constructor(
        private router: IRouter,
        private blogService: BlogService,
        private websiteService: WebsiteService,
        private eventAggregator: EventAggregator,
        private store: IStore<{}, UpdateMetaTitle | UpdateMetaDescription | UpdateOgType> = resolve(IStore),
        private helper: Helper,
        private nameFromShortcodeValueConverter: NameFromShortcodeValueConverter,
        private bannerImg: HTMLImageElement
    ) {
        this.baseAwsEndpoint = getAWSBucketEndpoint('blogs');
        this.DEVICES = DEVICES;
    }

    loading(params: Params, current: RouteNode) {
        this.params = params;
        this.currentRouteNode = current;
    }

    async attached() {
        const pages = await this.websiteService.getPagesByWebsiteShortcode();
        this.blogRoute = pages.find(x => x.name === 'Blog')?.routeName ?? 'blog';
        this.scrollToBlogpost();
        this.isAttached = true;
        this.blog = await this.blogService.getBlogBySlug(this.params.blog);
        if (!this.blog || (window.location.hostname.includes('resources') && !this.blog?.blogTags?.some(x => x?.blogTag?.name?.toLowerCase() === 'csr'))) {
            this.router.load('404');
            return;
        }
        const regex = /[<][p][>][<][b][r][>][<][/][p][>]/g;
        this.blog.content = this.blog.content?.replace(regex, '');
        const componentTagRegex = /&lt;([A-Z0-9]+) component&gt;/g;
        this.blog.content = this.blog.content?.replace(componentTagRegex, (_match: string, code: string) => {
            return `<cds-price-button code="${code}"></cds-price-button>`;
        });

        const blogTitle = `${this.blog.title} - ${this.nameFromShortcodeValueConverter.toView(this.shortCode)}`;
        this.currentRouteNode.title = blogTitle;
        this.store.dispatch({ type: ActionNames.UPDATE_META_TITLE, value: blogTitle });
        this.store.dispatch({ type: ActionNames.UPDATE_META_DESCRIPTION, value: this.blog.metaDescription });
        this.store.dispatch({ type: ActionNames.UPDATE_OG_TYPE, value: 'article' });
        this.readingTimeResult = this.readingTime();

        this.width = document.body.clientWidth;
        const categoryTag = this.blog.blogTag?.name || this.blog.blogTags?.[0]?.blogTag?.name || 'All';
        const href = this.blog.blogTag?.slug === null || this.blog.blogTag?.slug === undefined ? this.blog.blogTags?.[0]?.blogTag?.slug : this.blog.blogTag?.slug;
        const categoryHref = categoryTag === 'All' ? this.blogRoute : `category/${encodeURIComponent(href)}`;
        if (this.blog) {
            await this.blogService.updateBlogPostViewCounter(this.blog.id);
        }
        this.breadcrumbs = [
            { title: 'Blogs', href: '../' },
            { title: categoryTag, href: categoryHref },
            { title: this.blog.title, href: '', tooltip: this.blog.title.length > 80 }
        ];
        this.handleReviewSchema();
        this.handleEventSubscriptions();
        await this.pageChanged();
        this.scrollToTop();
        this.pageLoading = false;
    }

    detaching() {
        this.getReviewSchema()?.remove();
        this.sizeChanged?.dispose();
    }

    handleEventSubscriptions() {
        this.sizeChanged = this.eventAggregator.subscribe('size-changed', async payload => {
            this.width = payload.width;
            await this.pageChanged();
        });
    }

    async pageChanged() {
        if (this.isAttached) {
            this.blogs = await this.blogService.filterBlogs(
                {
                    page: this.page,
                    pageSize: this.perPage = 4,
                    published: true,
                    tag: await this.blog.blogTag === null ? this.blog.blogTags?.[0]?.blogTag?.name : this.blog.blogTag?.name,
                    websites: [this.shortCode]
                }
            );
            this.relatedArticles = await this.blogs.blogPosts;
            this.blogsCount = await this.blogs.totalCount;
            this.count = Math.ceil(this.blogsCount / this.perPage);
            if (this.page > this.count && this.count > 0) {
                this.page = this.count;
            }
        }
    }

    changeSelectHandler() {
        this.handleFocusOut();
    }

    handleFocusOut() {
        if (!this.arrowTriggered) return;
        this.arrowTriggered.style.transform = 'rotate(0deg)';
        this.arrowRotated = false;
        this.lastEventTriggered = 'focusOut';
        setTimeout(() => {
            this.lastEventTriggered = '';
        }, 200);
    }

    handleClick(e) {
        this.arrowTriggered = e.target.querySelector('.arrow-icon');
        this.arrowTriggered.style.transform = this.arrowRotated ? 'rotate(0deg)' : 'rotate(180deg)';
        this.arrowRotated = !this.arrowRotated;
    }

    handleReviewSchema() {
        this.getReviewSchema()?.remove();
        const reviewSchema = document.createElement('script');
        reviewSchema.setAttribute('id', 'article-schema');
        reviewSchema.type = 'application/id+json';
        reviewSchema.innerHTML = `
        {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": "${this.blog.title}",`;

        if (this.blog.previewImagePath) {
            reviewSchema.innerHTML += `"image": "${this.baseAwsEndpoint}${this.blog.previewImagePath}",`;
        }

        reviewSchema.innerHTML += `
            "datePublished": "${this.blog.createdDate}",
            "dateModified": "${this.blog.updatedDate}",`;

        if (this.blog.blogAuthor) {
            reviewSchema.innerHTML += `,
                "author": {
                    "@type": "Person",
                    "name": "${this.blog.blogAuthor.firstName + ' ' + this.blog.blogAuthor.lastName}",
                    "url": "${window.location.href}"
                }`;
        }

        reviewSchema.innerHTML += '}';

        document.head.appendChild(reviewSchema);
    }

    getReviewSchema = () => document.getElementById('article-schema');

    async tagClickHandler(tag) {
        await this.router.load(`category/${encodeURIComponent(tag.slug)}`);
    }

    readingTime() {
        const wordCount = this.blog.content?.split(' ').length;
        const readingTimeMinutes = Math.ceil(wordCount / 250);
        return readingTimeMinutes;
    }

    scrollToTop() {
        window.document.getElementById('navbar')?.scrollIntoView();
    }

    scrollToBlogpost() {
        window.document.getElementById('blogpost')?.scrollIntoView();
    }

    handlePagination(value: CustomEvent): void {
        return this.page = value.detail.page;
    }

    get bannerImageByWebsite() {
        const logo = {
            CG: '/logo/logo-cg.svg',
            CX: '/logo/logo-cx.svg',
            DS: '/logo/ds-logo-dark.svg',
            GT: '/logo/gt-logo-dark.svg'
        };

        return logo[this.shortCode] || 'UNDEFINED';
    }
}
